import { VCLossTypes } from 'components/underwriting/income-approach/constants';
import { Expenses } from './expenses';
import { CapRateConclusion } from './cap-rate-conclusion';

export enum CommercialLeaseStatusTypes {
  OCCUPIED = 'Occupied',
  VACANT = 'Vacant',
  OWNER_OCCUPIED = 'Owner Occupied',
}

export enum ResidentialLeaseStatusTypes {
  OCCUPIED = 'Occupied',
  VACANT = 'Vacant',
  EMPLOYEE = 'Employee Occupied',
}

export enum RentTypes {
  MARKET_RATE = 'Market Rate',
  RENT_STABILIZED = 'Rent Stabilized',
  RENT_CONTROLLED = 'Rent Controlled',
  SECTION_8 = 'Section 8',
  MONTH_TO_MONTH = 'Month to Month',
}

export enum UnitLayouts {
  TYPICAL = 'Typical',
  DUPLEX = 'Duplex',
  TRIPLEX = 'Triplex',
  SIMPLEX = 'Simplex',
  PENTHOUSE = 'Penthouse',
  LOFT = 'Loft',
  GARDEN_STYLE = 'Garden Style',
  BASEMENT = 'Basement',
  GARAGE = 'Garage',
  TOWNHOME = 'Townhome',
}

export enum OutdoorSpaces {
  NONE = 'None',
  BACKYARD = 'Backyard',
  BALCONY = 'Balcony',
  ROOF = 'Roof',
  TERRACE = 'Terrace',
}

export enum CommercialUses {
  RETAIL = 'Retail',
  OFFICE = 'Office',
  MEDICAL = 'Medical',
  COMMUNITY = 'Community',
  INDUSTRIAL = 'Industrial',
  UNDETERMINED = 'Undetermined',
  OTHER = 'Other',
}

export type RentRollUnit = ResidentialRentRollUnit | CommercialRentRollUnit;

export type ResidentialRentRollUnit = {
  id: string;
  number?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareFootage?: number;
  rent?: number;
  projectedRent?: number;
  outdoor?: OutdoorSpaces;
  rentType?: RentTypes;
  unitLayout?: UnitLayouts;
  leaseStatus?: ResidentialLeaseStatusTypes;
};

export type ResidentialRentComp = {
  address: string;
  bathrooms?: string;
  bedrooms: number;
  city: string;
  dateOfValue: string;
  position?: { lat: number; lng: number };
  monthlyRent: number;
  monthlyRentPerSF?: number;
  annualRent: number;
  annualRentPerSF?: number;
  squareFeet?: number;
  state: string;
  unitType?: string;
  zip?: string;
  unitNumber: string;
  leaseId: string;
  leaseVersionNumber?: number;
  isLatestVersion: boolean;
  status: string;
};

export interface CommercialRentRollUnit {
  number?: string;
  id: string;
  use?: CommercialUses;
  tenant?: string;
  squareFootage?: number;
  rent?: number;
  projectedRent?: number;
  leaseStatus?: CommercialLeaseStatusTypes;
}

export type CommercialRentComp = {
  leaseId: string;
  leaseVersionNumber?: number;
  isLatestVersion: boolean;
  status: string;
  address: string;
  city: string;
  state: string;
  zip?: string;
  fullAddressWithZip: string;
  tenantName: string;
  rentType: string;
  use: CommercialUses;
  monthlyRent: number;
  annualRent: number;
  annualRentPerSF: number;
  squareFeet: number;
  lease: {
    expiresAt?: string;
    signedAt: string;
    status: string;
    terms: string;
  };
};

export type ReimbursementType =
  | 'dollarAmount'
  | 'percentOfForecast'
  | 'increaseOverBaseYear'
  | 'percentOfCurrentYearLiability';

export type DollarAmountType = 'monthly' | 'annually';

export type CommercialReimbursementBase = {
  expenseCategory: string;
  reimbursementType: ReimbursementType;
  key: string;
  name: string;
  grossAnnualValue?: number;
  vcLoss?: number;
  grossVCLoss?: number;
  effectiveAnnualValue?: number;
};

export type DollarAmountReimbursement = CommercialReimbursementBase & {
  reimbursementType: 'dollarAmount';
  dollarAmountType: DollarAmountType;
  unitReimbursements: {
    unitId: string;
    tenant?: string;
    annual?: number;
    enabled?: boolean;
    monthly?: number;
  }[];
};

export type PercentOfForecastReimbursement = CommercialReimbursementBase & {
  reimbursementType: 'percentOfForecast';
  adminFeeKnown: boolean;
  unitReimbursements: {
    unitId: string;
    tenant?: string;
    annual?: number;
    enabled?: boolean;
    grossForecastedValue?: number;
    percentOfGrossForecastedValue?: number;
    annualLessAdminFee?: number;
    adminFeePercentage?: number;
    grossAdminFee?: number;
  }[];
};

export type BaseYearValueType = 'liability' | 'assessment';

export type IncreaseOverBaseYearReimbursement = CommercialReimbursementBase & {
  reimbursementType: 'increaseOverBaseYear';
  baseYearValueType: BaseYearValueType;
  unitReimbursements: {
    unitId: string;
    tenant?: string;
    annual?: number;
    enabled?: boolean;
    baseYear?: string;
    baseYearTaxRate?: number;
    baseYearAssessment?: number;
    baseYearLiability?: number;
    currentYearLiability?: number;
    increase?: number;
    share?: number;
  }[];
};

export type PercentOfCurrentYearLiabilityReimbursement = CommercialReimbursementBase & {
  reimbursementType: 'percentOfCurrentYearLiability';
  adminFeeKnown: boolean;
  unitReimbursements: {
    unitId: string;
    tenant?: string;
    annual?: number;
    enabled?: boolean;
    currentYearLiability?: number;
    percentOfCurrentYearLiability?: number;
    annualLessAdminFee?: number;
    adminFeePercentage?: number;
    grossAdminFee?: number;
  }[];
};

export type CommercialReimbursement =
  | DollarAmountReimbursement
  | PercentOfForecastReimbursement
  | IncreaseOverBaseYearReimbursement
  | PercentOfCurrentYearLiabilityReimbursement;

export interface RentRollTableColumn {
  data: string;
  value: string;
}

export interface OtherIncomeItem {
  id: string;
  vcLossType?: VCLossTypes;
  vcLossPercentage?: number;
  name?: string;
  amount?: number;
}

export interface IncomeApproach {
  residential?: Residential;
  commercial?: Commercial;
  expenses: Expenses;
  otherIncome: OtherIncomeItem[];
  taxes: any;
  capRateConclusion: CapRateConclusion;
}

export interface Residential {
  rentRoll: ResidentialRentRollUnit[];
  rentComps?: {
    comps: ResidentialRentComp[];
  };
  vcLossPercentage: number;
}

export interface Commercial {
  rentRoll: CommercialRentRollUnit[];
  rentComps?: {
    comps: CommercialRentComp[];
  };
  vcLossPercentage: number;
  reimbursements: CommercialReimbursement[];
}
